import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { NavEntryPoint, AdminNavEntryPointV2 } from '@retailer-platform/dashboard/routing'
import { v1Router, v3Router, flyerRouter, digitalFlyerRouter } from './routing/router'
import { retailerCollectionsAdminRouter } from './routing/retailerCollectionsAdminRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import { retailerCollectionsV3AccessControl } from './access-control/retailerCollectionsV3Access.configuration'
import { retailerCollectionsFlyersAccessControl } from './access-control/retailerCollectionsFlyersAccess.configuration'
import { omeletAccessControl } from './access-control/omeletAccess.configuration'
import { retailerCollectionsV2AdminAccessControl } from './access-control/retailerCollectionsV2AdminAccess.configuration'
import { Permission } from './access-control/permissions'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'retailer-collections',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'retailer-collections-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    scoped: [v1Router],
    partnerScoped: [v3Router, flyerRouter, digitalFlyerRouter],
    admin: [retailerCollectionsAdminRouter],
  },
  navigationEntries: [
    {
      attachToV2: NavEntryPoint.Merchandising,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Create, edit, and schedule featured product groups (collections) for merchandising on storefront pages',
          labelId: 'retailerCollectionsDomain.v3.list.title',
          route: 'retailer-collections-v3',
          accessControl: retailerCollectionsV3AccessControl,
          positionNavV2: 0,
          inToolPermissions: [
            {
              slug: Permission.RetailerCollectionsV2Modify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.RetailerCollectionsV2Modify,
            },
          ],
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.Marketing,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Create and manage promotional digital and print flyers for retailers',
          labelId: 'retailerCollectionsDomain.flyers.list.title',
          route: 'retailer-collections-flyers',
          accessControl: retailerCollectionsFlyersAccessControl,
          inToolPermissions: [
            {
              slug: Permission.FlyersModify,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.FlyersModify,
            },
          ],
          positionNavV2: 6,
        },
        {
          type: 'item',
          llmDescription: 'Manage omelet promotional templates and settings',
          labelId: 'retailerCollectionsDomain.flyers.omelet.title',
          route: 'retailer-collections-flyers-omelet',
          accessControl: omeletAccessControl,
          positionNavV2: 7,
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      attachToV2: AdminNavEntryPointV2.Merchandising,
      subNavItems: [
        {
          route: 'app-admin-retailer-collections-list',
          labelId: 'retailerCollectionsDomain.admin.collections.title',
          accessControl: retailerCollectionsV2AdminAccessControl,
          positionNavV2: 0,
        },
      ],
    },
  ],
}

export default definition
